import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX, MessageCircle } from "lucide-react";
import { VIDEO_URL, PROMO, waLink } from "@/data/site";

export const VideoReel = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section id="video" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <div className="grain-overlay" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h4 className="text-xs uppercase tracking-[0.3em] text-gold-500 font-sans mb-5">
            Guarda la scuola
          </h4>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-white leading-tight">
            La musica che
            <br />
            <span className="italic text-gold-500">prende vita.</span>
          </h2>
          <p className="mt-6 text-base text-gray-400 max-w-md">
            Un assaggio della vita alla Trinity Musical School: lezioni, saggi,
            palcoscenico ed emozioni. Iscrizioni aperte per l'anno {PROMO.schoolYear},
            con inizio delle lezioni il{" "}
            <span className="text-white font-medium">{PROMO.startLabel}</span>.
          </p>
          <a
            data-testid="video-whatsapp-btn"
            href={waLink("Ciao, ho visto il video sul sito e vorrei informazioni sui corsi 2026/2027")}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold-500 text-ink-900 font-semibold px-7 py-3.5 hover:bg-gold-400 hover:-translate-y-0.5 transition-transform duration-200 gold-glow"
          >
            <MessageCircle size={18} /> Contattaci per informazioni
          </a>
        </motion.div>

        {/* Video frame (portrait reel) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <div className="relative w-full max-w-[340px] aspect-[9/16] rounded-[2rem] border border-gold-500/30 bg-ink-800 overflow-hidden gold-glow">
            <video
              ref={ref}
              data-testid="reel-video"
              src={VIDEO_URL}
              className="h-full w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
            />
            {/* Controls */}
            <div className="absolute bottom-4 right-4 flex gap-2">
              <button
                data-testid="reel-play-toggle"
                onClick={togglePlay}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur text-white hover:bg-gold-500 hover:text-ink-900 transition-colors duration-200"
                aria-label={playing ? "Pausa" : "Play"}
              >
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <button
                data-testid="reel-mute-toggle"
                onClick={toggleMute}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur text-white hover:bg-gold-500 hover:text-ink-900 transition-colors duration-200"
                aria-label={muted ? "Attiva audio" : "Disattiva audio"}
              >
                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
